import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const IDLE_LIMIT_MS = 30 * 60 * 1000;   // log out after 30 minutes of no activity
const WARNING_BEFORE_MS = 60 * 1000;     // warn 1 minute before that

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];

export default function InactivityLogout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const warnTimer = useRef(null);
  const logoutTimer = useRef(null);
  const warnedRef = useRef(false);

  useEffect(() => {
    // Only track activity while actually logged in
    if (!user) return;

    const clearTimers = () => {
      if (warnTimer.current) clearTimeout(warnTimer.current);
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
    };

    const doLogout = () => {
      clearTimers();
      logout();
      navigate("/login");
      toast.info("You were signed out after a period of inactivity.");
    };

    const resetTimers = () => {
      clearTimers();
      warnedRef.current = false;
      warnTimer.current = setTimeout(() => {
        warnedRef.current = true;
        toast.warning("You'll be signed out in 1 minute due to inactivity.", { duration: 8000 });
      }, IDLE_LIMIT_MS - WARNING_BEFORE_MS);
      logoutTimer.current = setTimeout(doLogout, IDLE_LIMIT_MS);
    };

    resetTimers();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, resetTimers));

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, resetTimers));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return null;
}
